import { parseDateString } from "Helpers/dateTime";
import { UserEncoreStatus, UserEncoreTiers } from "Helpers/users";
import { getPackageStatus, isExpiredWithinDaysThreshold } from "./bannerHelpers";
import { EncorePackageProps } from "Types/vehicle";

export interface BannerState {
    status: UserEncoreStatus;
    packageExpiryDate?: string;
    packagePurchasePaylink?: string | null;
    hideCta?: boolean;
    disableCta?: boolean;
    showBanner: boolean;
    encoreTier?: UserEncoreTiers;
}

export interface BannerStates {
    bannerState: BannerState;
    extraBannerState?: { status: UserEncoreStatus; packageExpiryDate?: string; encoreTier?: UserEncoreTiers };
}

export interface BannerRuleContext {
    currentEncorePackage: EncorePackageProps;
    encoreTier: UserEncoreTiers;
    expiredPackages: EncorePackageProps[];
    upcomingPackages: EncorePackageProps[];
    membershipStatus: UserEncoreStatus;
    packageExpiryDate?: string;
    packagePurchasePaylink?: string | null;
    isActive: boolean;
}

// Evaluates necessary encore packages states
class BannerRuleEvaluator {
    private readonly mostRecentlyExpiredPackageValue: EncorePackageProps | undefined;
    private readonly hasBasicExtensionValue: boolean;
    private readonly onBasicExtensionValue: boolean;
    private readonly parallelBasicPackageValue: EncorePackageProps | undefined;
    private readonly parallelBasicPackageStatusValue: UserEncoreStatus | undefined;

    public constructor(private readonly context: BannerRuleContext) {
        this.mostRecentlyExpiredPackageValue = context.expiredPackages?.find(
            expiredPackage => expiredPackage !== context.currentEncorePackage
        );
        this.hasBasicExtensionValue = hasBasicExtension(context.upcomingPackages);
        this.onBasicExtensionValue = isOnBasicExtension(context.currentEncorePackage, context.expiredPackages);
        this.parallelBasicPackageValue = getOverlappingBasicPackage(context.upcomingPackages, context.expiredPackages);
        this.parallelBasicPackageStatusValue = this.parallelBasicPackageValue
            ? getPackageStatus(this.parallelBasicPackageValue)
            : undefined;
    }

    public get currentEncorePackage() {
        return this.context.currentEncorePackage;
    }

    public get encoreTier() {
        return this.currentEncorePackage.encoreTier;
    }

    public get expiredPackages() {
        return this.context.expiredPackages;
    }

    public get upcomingPackages() {
        return this.context.upcomingPackages;
    }

    public get membershipStatus() {
        return this.context.membershipStatus;
    }

    public get packageExpiryDate() {
        return this.context.packageExpiryDate;
    }

    public get packagePurchasePaylink() {
        return this.context.packagePurchasePaylink;
    }

    public get isActive() {
        return this.context.isActive;
    }

    public get mostRecentlyExpiredPackage() {
        return this.mostRecentlyExpiredPackageValue;
    }

    public get hasBasicExtension() {
        return this.hasBasicExtensionValue;
    }

    public get onBasicExtension() {
        return this.onBasicExtensionValue;
    }

    public get parallelBasicPackage() {
        return this.parallelBasicPackageValue;
    }

    public get parallelBasicPackageStatus() {
        return this.parallelBasicPackageStatusValue;
    }

    public createDefaultState(context: BannerRuleContext): BannerStates {
        return {
            bannerState: createBannerState(
                this.membershipStatus,
                context.encoreTier,
                this.packageExpiryDate,
                this.packagePurchasePaylink,
                !this.isActive
            ),
        };
    }
}

type BannerRule = (evaluator: BannerRuleEvaluator) => BannerStates | undefined;

// Creates the primary banner payload.
const createBannerState = (
    status: UserEncoreStatus,
    encoreTier: UserEncoreTiers,
    packageExpiryDate?: string,
    packagePurchasePaylink?: string | null,
    showBanner = true
): BannerState => ({
    status,
    packageExpiryDate,
    packagePurchasePaylink,
    showBanner,
    encoreTier,
});

// Identifies the Basic extension state that follows Platinum or Basic expiry.
const isOnBasicExtension = (currentEncorePackage: EncorePackageProps, expiredPackages: EncorePackageProps[]): boolean =>
    currentEncorePackage?.encoreTier === UserEncoreTiers.BASIC &&
    expiredPackages?.length > 0 &&
    [UserEncoreTiers.PLATINUM, UserEncoreTiers.BASIC].includes(expiredPackages[0].encoreTier);

// Checks whether any upcoming package keeps the user on a Basic plan.
export const hasBasicExtension = (upcomingPackages: EncorePackageProps[]): boolean =>
    upcomingPackages?.some(pkg => pkg.encoreTier === UserEncoreTiers.BASIC);

// Picks the nearest non-expired Basic package that can act as a parallel package.
const getOverlappingBasicPackage = (
    upcomingPackages: EncorePackageProps[],
    expiredPackages: EncorePackageProps[]
): EncorePackageProps | undefined => {
    const now = new Date();
    const candidatePackages = [...upcomingPackages, ...expiredPackages]
        .filter(pkg => {
            if (pkg.encoreTier !== UserEncoreTiers.BASIC) {
                return false;
            }

            const expiryDate = parseDateString(pkg.packageExpiryDate);

            return Boolean(expiryDate && expiryDate >= now);
        })
        .sort(
            (a, b) =>
                (parseDateString(a.packageExpiryDate)?.getTime() ?? Number.POSITIVE_INFINITY) -
                (parseDateString(b.packageExpiryDate)?.getTime() ?? Number.POSITIVE_INFINITY)
        );

    return candidatePackages[0];
};

// Shows an expired banner when a recent Platinum package rolled into a short Basic extension.
const resolveRecentPlatinumExpiryRule: BannerRule = evaluator => {
    const { mostRecentlyExpiredPackage, onBasicExtension, packagePurchasePaylink } = evaluator;
    const shouldShowRecentPlatinumExpiry =
        onBasicExtension &&
        mostRecentlyExpiredPackage?.encoreTier === UserEncoreTiers.PLATINUM &&
        isExpiredWithinDaysThreshold(mostRecentlyExpiredPackage.packageExpiryDate);

    if (!shouldShowRecentPlatinumExpiry || !mostRecentlyExpiredPackage) {
        return undefined;
    }

    return {
        bannerState: createBannerState(
            UserEncoreStatus.EXPIRED,
            mostRecentlyExpiredPackage.encoreTier,
            mostRecentlyExpiredPackage.packageExpiryDate,
            packagePurchasePaylink,
            true
        ),
    };
};

// Hides the expiring-soon Basic banner when another Basic package already follows it.
const resolveBasicUpcomingExtensionRule: BannerRule = evaluator => {
    const {
        encoreTier,
        membershipStatus,
        hasBasicExtension: hasUpcomingBasicExtension,
        packageExpiryDate,
        packagePurchasePaylink,
    } = evaluator;
    const isBasicMembershipExpiringWithExtension =
        encoreTier === UserEncoreTiers.BASIC &&
        membershipStatus === UserEncoreStatus.EXPIRING_SOON &&
        hasUpcomingBasicExtension;

    if (!isBasicMembershipExpiringWithExtension) {
        return undefined;
    }

    return {
        bannerState: createBannerState(
            membershipStatus,
            UserEncoreTiers.BASIC,
            packageExpiryDate,
            packagePurchasePaylink,
            false
        ),
    };
};

// Adds a Basic reminder banner after an Elevate membership has expired.
const resolveBasicAfterElevateExpiryRule: BannerRule = evaluator => {
    const { encoreTier, mostRecentlyExpiredPackage, parallelBasicPackage, membershipStatus, packageExpiryDate } =
        evaluator;

    const shouldShowElevateExpiredBannerWithBasicReminder =
        encoreTier === UserEncoreTiers.BASIC &&
        mostRecentlyExpiredPackage?.encoreTier === UserEncoreTiers.ELEVATE &&
        isExpiredWithinDaysThreshold(mostRecentlyExpiredPackage?.packageExpiryDate);

    if (!shouldShowElevateExpiredBannerWithBasicReminder || !mostRecentlyExpiredPackage) {
        return undefined;
    }

    return {
        bannerState: createBannerState(
            UserEncoreStatus.EXPIRED,
            UserEncoreTiers.ELEVATE,
            mostRecentlyExpiredPackage.packageExpiryDate,
            undefined,
            true
        ),
        extraBannerState:
            membershipStatus === UserEncoreStatus.EXPIRING_SOON && !parallelBasicPackage
                ? createBannerState(
                      UserEncoreStatus.EXPIRING_SOON,
                      UserEncoreTiers.BASIC,
                      packageExpiryDate,
                      undefined,
                      true
                  )
                : undefined,
    };
};

// Multi Membership Rules
const eligibleBannerRules: BannerRule[] = [
    resolveRecentPlatinumExpiryRule,
    resolveBasicAfterElevateExpiryRule,
    resolveBasicUpcomingExtensionRule,
];

// Resolves the first matching eligible banner rule, otherwise falls back to the default banner.
export const resolveEligibleBannerState = (context: BannerRuleContext): BannerStates => {
    const evaluator = new BannerRuleEvaluator(context);
    for (const rule of eligibleBannerRules) {
        const bannerState = rule(evaluator);

        if (bannerState) {
            return bannerState;
        }
    }

    return evaluator.createDefaultState(context);
};
